import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '@/shared/components/Layout';
import { api } from '@/shared/lib/api';
import { captureError } from '@/shared/lib/monitoring';
import { useTTS } from '@/shared/hooks/useTTS';
import { MetroTimeline } from '@/features/courses/MetroTimeline';
import { useBackHandler } from '@/shared/lib/navigation/BackNavigationContext';

interface CourseItem {
  id: number;
  slug: string;
  name: string;
  description: string;
  level: string;
  is_premium: boolean;
  order_index: number;
  total_words: number;
  completed_words: number;
  in_progress_words: number;
  due_reviews_count: number;
  percent: number;
  is_completed: boolean;
  total_lessons: number;
  completed_lessons?: number;
  current_lesson?: number;
}

interface CourseWordItem {
  course_word_id: number;
  order_index: number;
  word: string;
  translation: string;
  transcription?: string | null;
  russian_phonetic?: string | null;
  part_of_speech?: string | null;
  examples: Array<{ example_sentence?: string | null; example_translation?: string | null }>;
  lesson_index: number;
  status: 'completed' | 'known_from_dict' | 'learning' | 'new';
  is_completed: boolean;
  due_for_review: boolean;
  in_user_dictionary: boolean;
  mc_done: boolean;
  input_done: boolean;
  in_srs: boolean;
}

interface LessonItem {
  lesson_index: number;
  title: string;
  range_label: string;
  total_words: number;
  completed_words: number;
  mc_completed_words?: number;
  phase_remaining?: number;
  percent: number;
  is_completed: boolean;
  is_locked?: boolean;
  phase?: 'mc' | 'input' | 'completed';
}

interface CourseDetail {
  id: number;
  slug: string;
  name: string;
  description: string;
  level: string;
  is_premium: boolean;
  total_words: number;
  completed_words: number;
  percent: number;
  is_completed: boolean;
  due_reviews_count: number;
  total_lessons: number;
  current_lesson: number;
  lessons: LessonItem[];
  words: CourseWordItem[];
}

const LEVEL_EMOJIS: Record<string, string> = {
  Top100: '🚀',
  Top500: '⭐',
  A1: '🥉',
  A2: '📗',
  B1: '🥈',
  B2: '📘',
  C1: '🥇',
  C2: '💎',
};

const LEVEL_COLORS: Record<string, string> = {
  Top100: '#f59e0b',
  Top500: '#8b5cf6',
  A1: '#10b981',
  A2: '#06b6d4',
  B1: '#3b82f6',
  B2: '#6366f1',
  C1: '#ec4899',
  C2: '#f43f5e',
};

export function CoursesPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { speak } = useTTS();

  const [courses, setCourses] = useState<CourseItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeCourse, setActiveCourse] = useState<CourseDetail | null>(null);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);

  // Категория на главном экране: 'all' | 'tops' | 'cefr'
  const [categoryFilter, setCategoryFilter] = useState<'all' | 'tops' | 'cefr'>('all');

  // Режим объёма курса: 'express' (150 слов / 10 уроков) | 'all' (все слова из базы)
  const [scopeFilter, setScopeFilter] = useState<'express' | 'all'>('express');

  // Вкладка детального курса: 'roadmap' (Карта уроков) | 'words' (Слова курса)
  const [courseTab, setCourseTab] = useState<'roadmap' | 'words'>('roadmap');

  // Фильтры внутри детального экрана курса
  const [selectedLesson, setSelectedLesson] = useState<number | null>(null);
  const [wordSearch, setWordSearch] = useState('');
  const [wordFilter, setWordFilter] = useState<'all' | 'new' | 'learning' | 'completed'>('all');

  // Тост уведомлений
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  // Модалка подтверждения сброса урока
  const [showResetModal, setShowResetModal] = useState<number | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  const handleResetLesson = async (lessonIndex: number) => {
    if (!activeCourse || isResetting) return;
    setIsResetting(true);
    try {
      await api.post(`/courses/${activeCourse.id}/lessons/${lessonIndex}/reset`, {});
      setShowResetModal(null);
      showToast(`Урок ${lessonIndex} сброшен! Можно пройти с чистого листа 🔄`);
      const detail = await api.get<CourseDetail>(`/courses/${activeCourse.id}`);
      setActiveCourse(detail);
      loadCourses();
    } catch (err) {
      console.error(err);
      showToast('Не удалось сбросить урок');
    } finally {
      setIsResetting(false);
    }
  };

  const loadCourses = useCallback(async () => {
    try {
      const data = await api.get<CourseItem[]>('/courses');
      setCourses(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Ошибка загрузки курсов:', err);
      captureError(err, { context: 'CoursesPage.loadCourses' });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCourses();
  }, [loadCourses]);

  const openCourse = useCallback(async (id: number) => {
    setIsLoadingDetail(true);
    try {
      const detail = await api.get<CourseDetail>(`/courses/${id}`);
      setActiveCourse(detail);
      setSelectedLesson(null);
      setWordSearch('');
      setWordFilter('all');
      setScopeFilter('express'); // по умолчанию компактный экспресс
      setCourseTab('roadmap');
      // Обновляем query param
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        next.set('open', id.toString());
        return next;
      }, { replace: true });
    } catch (err) {
      console.error('Ошибка открытия курса:', err);
      captureError(err, { context: 'CoursesPage.openCourse', course_id: id });
      showToast('Не удалось загрузить данные курса');
    } finally {
      setIsLoadingDetail(false);
    }
  }, [setSearchParams]);

  // Автооткрытие курса из query param `open`
  useEffect(() => {
    const openId = searchParams.get('open');
    if (openId && !activeCourse && !isLoadingDetail) {
      const idNum = parseInt(openId, 10);
      if (idNum) openCourse(idNum);
    }
  }, [searchParams, activeCourse, isLoadingDetail, openCourse]);

  const handleCloseCourse = () => {
    setActiveCourse(null);
    setSelectedLesson(null);
    setWordSearch('');
    setCourseTab('roadmap');
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('open');
      return next;
    }, { replace: true });
    loadCourses();
  };

  // Если открыта карточка курса — свайп назад или BackButton Telegram закрывает её
  useBackHandler(() => {
    handleCloseCourse();
  }, Boolean(activeCourse), 5);

  // Старт сессии изучения
  const handleStartStudy = (lessonIndex?: number) => {
    if (!activeCourse) return;
    const targetLesson = lessonIndex ?? activeCourse.current_lesson;
    const lessonObj = activeCourse.lessons.find(l => l.lesson_index === targetLesson);
    const mode = lessonObj?.phase === 'input' ? 'input' : 'mc';
    navigate(`/study?course_id=${activeCourse.id}&course_name=${encodeURIComponent(activeCourse.name)}&mode=${mode}&lesson=${targetLesson}`);
  };

  // Повторение изученного в курсе
  const handleStartReview = () => {
    if (!activeCourse) return;
    navigate(`/study?course_id=${activeCourse.id}&course_name=${encodeURIComponent(activeCourse.name)}&mode=srs`);
  };

  // Пропустить слово курса («Уже знаю»)
  const handleSkipWord = async (cwId: number) => {
    if (!activeCourse) return;
    try {
      await api.post(`/courses/${activeCourse.id}/words/${cwId}/skip`, {});
      setActiveCourse(prev => {
        if (!prev) return null;
        const updatedWords = prev.words.map(w => {
          if (w.course_word_id === cwId) {
            return {
              ...w,
              is_completed: true,
              status: 'completed' as const,
              input_done: true,
              mc_done: true,
              in_srs: true,
            };
          }
          return w;
        });
        const completedCount = updatedWords.filter(w => w.is_completed).length;
        const pct = Math.round((completedCount / prev.total_words) * 100);

        // Обновляем прогресс уроков
        let nextCurrentLesson = 1;
        const updatedLessons = prev.lessons.map((l, i) => {
          const lWords = updatedWords.filter(w => w.lesson_index === l.lesson_index);
          const lDone = lWords.filter(w => w.is_completed).length;
          const isDone = lDone === lWords.length && lWords.length > 0;
          return {
            ...l,
            completed_words: lDone,
            percent: lWords.length > 0 ? Math.round((lDone / lWords.length) * 100) : 0,
            is_completed: isDone,
          };
        });

        const foundCurrent = updatedLessons.find(l => !l.is_completed);
        nextCurrentLesson = foundCurrent ? foundCurrent.lesson_index : updatedLessons.length;

        // Пересчитываем замки
        for (let i = 0; i < updatedLessons.length; i++) {
          const isLocked = i > 0 && !updatedLessons[i - 1].is_completed && updatedLessons[i].lesson_index > nextCurrentLesson;
          updatedLessons[i].is_locked = isLocked;
        }

        return {
          ...prev,
          words: updatedWords,
          completed_words: completedCount,
          percent: pct,
          current_lesson: nextCurrentLesson,
          lessons: updatedLessons,
          is_completed: completedCount >= prev.total_words,
        };
      });
      showToast('Слово отмечено как освоенное! ✓');
    } catch (err: any) {
      showToast(err.message || 'Ошибка обновления слова');
    }
  };

  // Сбросить статус слова курса
  const handleResetWord = async (cwId: number) => {
    if (!activeCourse) return;
    try {
      await api.post(`/courses/${activeCourse.id}/words/${cwId}/unskip`, {});
      setActiveCourse(prev => {
        if (!prev) return null;
        const updatedWords = prev.words.map(w => {
          if (w.course_word_id === cwId) {
            return {
              ...w,
              is_completed: false,
              status: 'new' as const,
              input_done: false,
              mc_done: false,
              in_srs: false,
            };
          }
          return w;
        });
        const completedCount = updatedWords.filter(w => w.is_completed).length;
        const pct = Math.round((completedCount / prev.total_words) * 100);

        const updatedLessons = prev.lessons.map(l => {
          const lWords = updatedWords.filter(w => w.lesson_index === l.lesson_index);
          const lDone = lWords.filter(w => w.is_completed).length;
          return {
            ...l,
            completed_words: lDone,
            percent: lWords.length > 0 ? Math.round((lDone / lWords.length) * 100) : 0,
            is_completed: false,
          };
        });

        const foundCurrent = updatedLessons.find(l => !l.is_completed);
        const nextCurrentLesson = foundCurrent ? foundCurrent.lesson_index : 1;

        for (let i = 0; i < updatedLessons.length; i++) {
          const isLocked = i > 0 && !updatedLessons[i - 1].is_completed && updatedLessons[i].lesson_index > nextCurrentLesson;
          updatedLessons[i].is_locked = isLocked;
        }

        return {
          ...prev,
          words: updatedWords,
          completed_words: completedCount,
          percent: pct,
          current_lesson: nextCurrentLesson,
          lessons: updatedLessons,
          is_completed: false,
        };
      });
      showToast('Слово возвращено в обучение');
    } catch (err: any) {
      showToast(err.message || 'Ошибка');
    }
  };

  // Добавить слово курса в личный словарь («➕ В словарь»)
  const handleAddToDictionary = async (cwId: number) => {
    if (!activeCourse) return;
    try {
      const res = await api.post<{ added: boolean; message: string }>(
        `/courses/${activeCourse.id}/words/${cwId}/add-to-dictionary`,
        {}
      );
      if (res.added) {
        setActiveCourse(prev => {
          if (!prev) return null;
          return {
            ...prev,
            words: prev.words.map(w => w.course_word_id === cwId ? { ...w, in_user_dictionary: true } : w),
          };
        });
        showToast(res.message || 'Слово добавлено в ваш словарь! 📝');
      } else {
        showToast(res.message || 'Слово уже есть в словаре');
      }
    } catch (err: any) {
      showToast(err.message || 'Ошибка добавления');
    }
  };

  // Фильтрованные курсы на главном экране
  const filteredCourses = useMemo(() => {
    if (categoryFilter === 'tops') {
      return courses.filter(c => c.slug.startsWith('top'));
    }
    if (categoryFilter === 'cefr') {
      return courses.filter(c => !c.slug.startsWith('top'));
    }
    return courses;
  }, [courses, categoryFilter]);

  // Уроки с учётом режима «Экспресс» vs «Все слова»
  const displayedLessons = useMemo(() => {
    if (!activeCourse) return [];
    const isLevelCourse = !activeCourse.slug.startsWith('top');
    if (isLevelCourse && scopeFilter === 'express') {
      return activeCourse.lessons.slice(0, 10); // первые 10 уроков (150 слов)
    }
    return activeCourse.lessons;
  }, [activeCourse, scopeFilter]);

  // Расчёт прогресса с учётом выбранного режима объема
  const scopeProgress = useMemo(() => {
    if (!activeCourse) return { total: 0, completed: 0, percent: 0, is_completed: false };
    const isLevelCourse = !activeCourse.slug.startsWith('top');

    if (isLevelCourse && scopeFilter === 'express') {
      const expressWords = activeCourse.words.filter(w => w.lesson_index <= 10);
      const total = expressWords.length;
      const completed = expressWords.filter(w => w.is_completed).length;
      const percent = total > 0 ? Math.round((completed / total) * 100) : 0;
      return { total, completed, percent, is_completed: completed >= total && total > 0 };
    }

    return {
      total: activeCourse.total_words,
      completed: activeCourse.completed_words,
      percent: activeCourse.percent,
      is_completed: activeCourse.is_completed,
    };
  }, [activeCourse, scopeFilter]);

  // Фильтрованные слова внутри активного курса
  const filteredWords = useMemo(() => {
    if (!activeCourse) return [];
    let list = activeCourse.words;

    const isLevelCourse = !activeCourse.slug.startsWith('top');
    if (isLevelCourse && scopeFilter === 'express') {
      list = list.filter(w => w.lesson_index <= 10);
    }

    if (selectedLesson !== null) {
      list = list.filter(w => w.lesson_index === selectedLesson);
    }

    if (wordFilter === 'new') {
      list = list.filter(w => w.status === 'new');
    } else if (wordFilter === 'learning') {
      list = list.filter(w => w.status === 'learning');
    } else if (wordFilter === 'completed') {
      list = list.filter(w => w.is_completed);
    }

    if (wordSearch.trim()) {
      const q = wordSearch.trim().toLowerCase().replace(/ё/g, 'е');
      list = list.filter(w =>
        w.word.toLowerCase().includes(q) ||
        w.translation.toLowerCase().replace(/ё/g, 'е').includes(q)
      );
    }

    return list;
  }, [activeCourse, scopeFilter, selectedLesson, wordFilter, wordSearch]);

  if (isLoading) {
    return (
      <Layout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', color: 'var(--theme-text-secondary)' }}>
          Загрузка курсов...
        </div>
      </Layout>
    );
  }

  // ---------------------------------------------------------------------------
  // Экран просмотра курса (детальный вид с уроками и словами)
  // ---------------------------------------------------------------------------
  if (activeCourse) {
    const color = LEVEL_COLORS[activeCourse.level] ?? 'var(--theme-primary)';
    const emoji = LEVEL_EMOJIS[activeCourse.level] ?? '📚';
    const isLevelCourse = !activeCourse.slug.startsWith('top');

    return (
      <Layout showNav={false}>
        <div style={{ padding: '16px 16px 80px' }}>
          {/* Верхняя панель навигации */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 14 }}>
            <button
              onClick={handleCloseCourse}
              style={{
                background: 'var(--theme-bg-elevated)',
                border: '1px solid var(--theme-border)',
                borderRadius: 10,
                padding: '8px 12px',
                color: 'var(--theme-text)',
                fontSize: 14,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <span>←</span>
              <span>Курсы</span>
            </button>
            <div style={{ flex: 1 }} />
            <div style={{
              background: `${color}20`,
              color,
              border: `1px solid ${color}`,
              borderRadius: 8,
              padding: '4px 10px',
              fontSize: 12,
              fontWeight: 700,
            }}>
              {activeCourse.level}
            </div>
          </div>

          {/* Карточка курса (Header) */}
          <div style={{
            background: 'var(--theme-bg-card)',
            border: '1px solid var(--theme-border)',
            borderRadius: 16,
            padding: 16,
            marginBottom: 14,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                fontSize: 34,
                width: 52,
                height: 52,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: `${color}18`,
                borderRadius: 14,
                flexShrink: 0,
              }}>
                {emoji}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h1 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--theme-text)' }}>
                  {activeCourse.name}
                </h1>
                <div style={{ fontSize: 12, color: 'var(--theme-text-secondary)', marginTop: 3 }}>
                  {activeCourse.description}
                </div>
              </div>
            </div>

            {/* Переключатель объема курса (Экспресс / Все слова) для CEFR уровней */}
            {isLevelCourse && (
              <div style={{
                display: 'flex',
                background: 'var(--theme-bg-elevated)',
                borderRadius: 10,
                padding: 3,
                marginTop: 14,
                border: '1px solid var(--theme-border)',
              }}>
                <button
                  onClick={() => { setScopeFilter('express'); setSelectedLesson(null); }}
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    borderRadius: 8,
                    border: 'none',
                    background: scopeFilter === 'express' ? color : 'transparent',
                    color: scopeFilter === 'express' ? '#fff' : 'var(--theme-text-secondary)',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                  }}
                >
                  ⚡ Экспресс (150 слов)
                </button>
                <button
                  onClick={() => { setScopeFilter('all'); setSelectedLesson(null); }}
                  style={{
                    flex: 1,
                    padding: '6px 8px',
                    borderRadius: 8,
                    border: 'none',
                    background: scopeFilter === 'all' ? color : 'transparent',
                    color: scopeFilter === 'all' ? '#fff' : 'var(--theme-text-secondary)',
                    fontSize: 12,
                    fontWeight: 600,
                    cursor: 'pointer',
                    transition: 'background 0.2s ease',
                  }}
                >
                  📚 Все слова ({activeCourse.total_words})
                </button>
              </div>
            )}

            {/* Прогресс курса */}
            <div style={{ marginTop: 14 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, marginBottom: 6 }}>
                <span style={{ color: 'var(--theme-text-secondary)' }}>
                  {scopeProgress.is_completed
                    ? isLevelCourse && scopeFilter === 'express'
                      ? '⚡ Экспресс-трек освоен!'
                      : '🏆 Полный курс освоен!'
                    : isLevelCourse && scopeFilter === 'express'
                      ? 'Прогресс Экспресса (150 слов)'
                      : 'Прогресс курса'}
                </span>
                <span style={{ fontWeight: 700, color: scopeProgress.is_completed ? '#10b981' : color }}>
                  {scopeProgress.completed} / {scopeProgress.total} ({scopeProgress.percent}%)
                </span>
              </div>
              <div style={{ height: 8, background: 'var(--theme-border)', borderRadius: 4, overflow: 'hidden' }}>
                <div style={{
                  height: '100%',
                  background: scopeProgress.is_completed ? '#10b981' : color,
                  width: `${scopeProgress.percent}%`,
                  borderRadius: 4,
                  transition: 'width 0.4s ease',
                }} />
              </div>
            </div>

            {/* Подсказка о переходе на полный курс при завершении Экспресса */}
            {isLevelCourse && scopeFilter === 'express' && scopeProgress.is_completed && (
              <div style={{
                marginTop: 12,
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                borderRadius: 10,
                padding: '10px 14px',
                fontSize: 12,
                color: 'var(--theme-text)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
              }}>
                <div>
                  <div style={{ fontWeight: 700, color: '#10b981', marginBottom: 2 }}>⚡ Экспресс пройден!</div>
                  <div style={{ color: 'var(--theme-text-secondary)', fontSize: 11 }}>
                    В полном курсе ещё {Math.max(0, activeCourse.total_words - scopeProgress.completed)} слов ({Math.max(0, activeCourse.total_lessons - 10)} уроков).
                  </div>
                </div>
                <button
                  onClick={() => { setScopeFilter('all'); setSelectedLesson(null); }}
                  style={{
                    padding: '6px 12px',
                    background: '#10b981',
                    color: '#fff',
                    border: 'none',
                    borderRadius: 8,
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                  }}
                >
                  Все слова →
                </button>
              </div>
            )}

            {/* Главные кнопки действий */}
            {(() => {
              const isExpressComplete = isLevelCourse && scopeFilter === 'express' && scopeProgress.is_completed;
              const targetL = selectedLesson ?? (isExpressComplete ? 1 : activeCourse.current_lesson);
              const lessonObj = activeCourse.lessons.find(l => l.lesson_index === targetL);
              const isStep2 = lessonObj?.phase === 'input';
              const isLessonComplete = lessonObj?.is_completed;
              const canReset = lessonObj && (lessonObj.completed_words > 0 || (lessonObj.mc_completed_words ?? 0) > 0 || isLessonComplete);
              const hasReviews = activeCourse.due_reviews_count > 0;
              const hasSecondaryRow = canReset || hasReviews;

              const mainButtonText = selectedLesson
                ? isStep2
                  ? `Закрепить вводом (Урок ${selectedLesson})`
                  : isLessonComplete
                    ? `Повторить Урок ${selectedLesson}`
                    : `Учить Урок ${selectedLesson}`
                : isStep2
                  ? `Закрепить (Урок ${activeCourse.current_lesson}: Ввод)`
                  : scopeProgress.is_completed
                    ? isLevelCourse && scopeFilter === 'express'
                      ? 'Повторить Экспресс (Урок 1)'
                      : 'Пройти курс заново'
                    : `Учить (Урок ${activeCourse.current_lesson}: Тест)`;

              return (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 16 }}>
                  {/* Основная кнопка обучения (полная ширина) */}
                  <button
                    onClick={() => handleStartStudy(targetL)}
                    style={{
                      width: '100%',
                      padding: '13px 16px',
                      background: color,
                      color: '#fff',
                      border: 'none',
                      borderRadius: 12,
                      fontSize: 15,
                      fontWeight: 700,
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 8,
                      boxShadow: `0 4px 14px ${color}40`,
                      boxSizing: 'border-box',
                    }}
                  >
                    <span>{isStep2 ? '✍️' : '🚀'}</span>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {mainButtonText}
                    </span>
                  </button>

                  {/* Дополнительные действия: повторение SRS и/или сброс */}
                  {hasSecondaryRow && (
                    <div style={{ display: 'flex', gap: 8, width: '100%', boxSizing: 'border-box' }}>
                      {hasReviews && (
                        <button
                          onClick={handleStartReview}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            padding: '11px 6px',
                            background: 'rgba(245, 158, 11, 0.15)',
                            color: '#f59e0b',
                            border: '1px solid #f59e0b',
                            borderRadius: 12,
                            fontSize: 12.5,
                            fontWeight: 700,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            whiteSpace: 'nowrap',
                            boxSizing: 'border-box',
                          }}
                          title="Повторить слова курса, готовые к повторению"
                        >
                          <span>🔄</span>
                          <span>
                            Повторить ({activeCourse.due_reviews_count})
                          </span>
                        </button>
                      )}

                      {canReset && (
                        <button
                          onClick={() => setShowResetModal(targetL)}
                          style={{
                            flex: 1,
                            minWidth: 0,
                            padding: '11px 6px',
                            background: 'var(--theme-bg-elevated)',
                            color: 'var(--theme-text-secondary)',
                            border: '1px solid var(--theme-border)',
                            borderRadius: 12,
                            fontSize: 12.5,
                            fontWeight: 600,
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: 4,
                            whiteSpace: 'nowrap',
                            boxSizing: 'border-box',
                          }}
                          title={`Сбросить прогресс Урока ${targetL} и пройти с чистого листа`}
                        >
                          <span>↺</span>
                          <span>Начать заново</span>
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })()}
          </div>

          {/* Подсказка в 1 строчку об устройстве обучения */}
          <div style={{
            background: 'var(--theme-bg-elevated)',
            border: '1px solid var(--theme-border)',
            borderRadius: 10,
            padding: '8px 12px',
            marginBottom: 16,
            fontSize: 12,
            color: 'var(--theme-text-secondary)',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}>
            <span style={{ fontSize: 16, flexShrink: 0 }}>💡</span>
            <span>
              Учим слова пошагово: сначала знакомимся в тесте (выбор перевода), затем закрепляем вводом на клавиатуре. Уроки открываются по порядку!
            </span>
          </div>

          {/* Вкладки: Карта уроков vs Слова курса */}
          <div style={{
            display: 'flex',
            background: 'var(--theme-bg-elevated)',
            borderRadius: 12,
            padding: 4,
            marginBottom: 16,
            border: '1px solid var(--theme-border)',
            gap: 4,
          }}>
            <button
              onClick={() => setCourseTab('roadmap')}
              style={{
                flex: 1,
                padding: '10px 14px',
                borderRadius: 10,
                border: 'none',
                background: courseTab === 'roadmap' ? color : 'transparent',
                color: courseTab === 'roadmap' ? '#fff' : 'var(--theme-text-secondary)',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                transition: 'all 0.2s ease',
              }}
            >
              <span>🗺️</span>
              <span>Карта уроков</span>
            </button>
            <button
              onClick={() => setCourseTab('words')}
              style={{
                flex: 1,
                padding: '10px 14px',
                borderRadius: 10,
                border: 'none',
                background: courseTab === 'words' ? color : 'transparent',
                color: courseTab === 'words' ? '#fff' : 'var(--theme-text-secondary)',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                transition: 'all 0.2s ease',
              }}
            >
              <span>📝</span>
              <span>Слова курса ({scopeProgress.total})</span>
            </button>
          </div>

          {/* Контент активной вкладки */}
          {courseTab === 'roadmap' ? (
            <MetroTimeline
              lessons={displayedLessons}
              words={activeCourse.words}
              currentLessonIndex={activeCourse.current_lesson}
              color={color}
              onStartLesson={handleStartStudy}
              onResetLesson={(idx) => setShowResetModal(idx)}
              onViewWords={(idx) => {
                setSelectedLesson(idx);
                setCourseTab('words');
              }}
            />
          ) : (
            <div>
              {/* Плашка фильтра по уроку, если перешли из карты уроков */}
              {selectedLesson !== null && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '10px 14px',
                  background: `${color}15`,
                  border: `1.5px solid ${color}40`,
                  borderRadius: 12,
                  marginBottom: 12,
                  fontSize: 13,
                }}>
                  <span style={{ fontWeight: 600, color: 'var(--theme-text)' }}>
                    📖 Показаны слова Урока {selectedLesson}
                  </span>
                  <button
                    onClick={() => setSelectedLesson(null)}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: color,
                      fontWeight: 700,
                      fontSize: 12,
                      cursor: 'pointer',
                    }}
                  >
                    Показать все ✕
                  </button>
                </div>
              )}

              {/* Слова курса: поиск и фильтры */}
              <div style={{ marginBottom: 12 }}>
                {/* Поиск */}
                <div style={{ position: 'relative', marginBottom: 10 }}>
                  <input
                    type="text"
                    value={wordSearch}
                    onChange={e => setWordSearch(e.target.value)}
                    placeholder="🔍 Поиск слов в курсе..."
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      background: 'var(--theme-bg-elevated)',
                      border: '1px solid var(--theme-border)',
                      borderRadius: 10,
                      color: 'var(--theme-text)',
                      fontSize: 14,
                      outline: 'none',
                      boxSizing: 'border-box',
                    }}
                  />
                  {wordSearch && (
                    <button
                      onClick={() => setWordSearch('')}
                      style={{
                        position: 'absolute',
                        right: 8,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        color: 'var(--theme-text-muted)',
                        cursor: 'pointer',
                        fontSize: 14,
                        padding: 4,
                      }}
                    >
                      ✕
                    </button>
                  )}
                </div>

                {/* Фильтр-чипы статусов */}
                <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
                  {(['all', 'new', 'learning', 'completed'] as const).map(tab => {
                    const labels: Record<string, string> = {
                      all: 'Все',
                      new: 'Новые',
                      learning: 'В процессе',
                      completed: 'Освоены',
                    };
                    const isActive = wordFilter === tab;
                    return (
                      <button
                        key={tab}
                        onClick={() => setWordFilter(tab)}
                        style={{
                          padding: '5px 12px',
                          borderRadius: 999,
                          border: `1px solid ${isActive ? color : 'var(--theme-border)'}`,
                          background: isActive ? `${color}20` : 'var(--theme-bg-elevated)',
                          color: isActive ? color : 'var(--theme-text-secondary)',
                          fontSize: 12,
                          fontWeight: isActive ? 600 : 400,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {labels[tab]}
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Список слов */}
              <div style={{
                background: 'var(--theme-bg-card)',
                border: '1px solid var(--theme-border)',
                borderRadius: 14,
                overflow: 'hidden',
              }}>
                {filteredWords.length === 0 ? (
                  <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--theme-text-muted)', fontSize: 13 }}>
                    Нет подходящих слов
                  </div>
                ) : (
                  filteredWords.map(w => {
                    return (
                      <div
                        key={w.course_word_id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 10,
                          padding: '12px 14px',
                          borderBottom: '1px solid var(--theme-border)',
                          position: 'relative',
                        }}
                      >
                        {/* Индикатор статуса */}
                        <div style={{
                          width: 4,
                          alignSelf: 'stretch',
                          borderRadius: 2,
                          background: w.status === 'known_from_dict'
                            ? '#8b5cf6'
                            : w.is_completed
                              ? '#10b981'
                              : w.status === 'learning'
                                ? '#3b82f6'
                                : 'var(--theme-border)',
                          flexShrink: 0,
                        }} />

                        {/* Слово и перевод */}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
                            <span style={{
                              fontWeight: 600,
                              fontSize: 15,
                              color: 'var(--theme-text)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {w.word}
                            </span>
                            <button
                              type="button"
                              onClick={() => speak(w.word)}
                              style={{
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: '2px 4px',
                                fontSize: 14,
                                lineHeight: 1,
                                color: 'var(--theme-text-muted)',
                                flexShrink: 0,
                              }}
                              aria-label="Озвучить"
                            >
                              🔊
                            </button>
                          </div>
                          {w.transcription && (
                            <div style={{
                              fontSize: 11,
                              color: 'var(--theme-text-muted)',
                              fontFamily: 'monospace',
                              lineHeight: 1.25,
                              marginTop: 2,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}>
                              {w.transcription}
                            </div>
                          )}
                          <div style={{
                            fontSize: 13,
                            color: 'var(--theme-text-secondary)',
                            marginTop: 2,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}>
                            {w.translation}
                          </div>
                        </div>

                        {/* Бейджи статуса */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0, marginLeft: 'auto' }}>
                          {w.status === 'known_from_dict' && (
                            <span style={{
                              background: 'rgba(139, 92, 246, 0.15)',
                              color: '#8b5cf6',
                              borderRadius: 6,
                              padding: '2px 6px',
                              fontSize: 11,
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                            }}>
                              Из словаря
                            </span>
                          )}

                          {w.is_completed && w.status !== 'known_from_dict' && (
                            <span style={{
                              color: '#10b981',
                              fontSize: 14,
                              fontWeight: 700,
                            }}>
                              ✓
                            </span>
                          )}

                          {/* Кнопка «Уже знаю» (если ещё не освоено) */}
                          {!w.is_completed && (
                            <button
                              onClick={() => handleSkipWord(w.course_word_id)}
                              style={{
                                background: 'var(--theme-bg-elevated)',
                                border: '1px solid var(--theme-border)',
                                borderRadius: 8,
                                padding: '4px 8px',
                                fontSize: 11,
                                color: 'var(--theme-text-secondary)',
                                cursor: 'pointer',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                              }}
                              title="Сдать экстерном (уже знаю)"
                            >
                              Знаю ✓
                            </button>
                          )}

                          {/* Если завершено — можно сбросить при желании */}
                          {w.is_completed && w.status !== 'known_from_dict' && (
                            <button
                              onClick={() => handleResetWord(w.course_word_id)}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: 'var(--theme-text-muted)',
                                fontSize: 11,
                                cursor: 'pointer',
                                padding: '2px 4px',
                              }}
                              title="Сбросить статус"
                            >
                              ✕
                            </button>
                          )}

                          {/* Кнопка «➕ В словарь» */}
                          {!w.in_user_dictionary ? (
                            <button
                              onClick={() => handleAddToDictionary(w.course_word_id)}
                              style={{
                                background: 'rgba(59, 130, 246, 0.12)',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                borderRadius: 8,
                                padding: '4px 8px',
                                fontSize: 11,
                                color: '#3b82f6',
                                cursor: 'pointer',
                                fontWeight: 600,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                whiteSpace: 'nowrap',
                              }}
                              title="Добавить в мой личный словарь"
                            >
                              <span>➕</span>
                              <span>В словарь</span>
                            </button>
                          ) : (
                            <span style={{
                              fontSize: 10,
                              color: 'var(--theme-text-muted)',
                              padding: '2px 4px',
                              whiteSpace: 'nowrap',
                            }}>
                              В словаре
                            </span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          )}
        </div>

        {/* Модалка сброса урока */}
        {showResetModal !== null && (
          <div style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0, 0, 0, 0.7)',
            backdropFilter: 'blur(4px)',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}>
            <div style={{
              background: 'var(--theme-bg-elevated)',
              border: '1px solid var(--theme-border)',
              borderRadius: 20,
              padding: '24px 20px',
              maxWidth: 360,
              width: '100%',
              textAlign: 'center',
              boxShadow: '0 12px 36px rgba(0, 0, 0, 0.4)',
            }}>
              <div style={{ fontSize: 44, marginBottom: 12 }}>🔄</div>
              <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-text)', marginBottom: 8 }}>
                Пройти Урок {showResetModal} заново?
              </h3>
              <p style={{ fontSize: 13, color: 'var(--theme-text-secondary)', lineHeight: 1.5, marginBottom: 20 }}>
                Прогресс по словам этого урока будет сброшен. Вы сможете пройти его с чистого листа: сначала Тест, затем Ввод на клавиатуре.
              </p>
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  onClick={() => setShowResetModal(null)}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'var(--theme-bg-card)',
                    border: '1px solid var(--theme-border)',
                    borderRadius: 12,
                    color: 'var(--theme-text-secondary)',
                    fontSize: 14,
                    fontWeight: 600,
                    cursor: 'pointer',
                  }}
                >
                  Отмена
                </button>
                <button
                  onClick={() => handleResetLesson(showResetModal)}
                  disabled={isResetting}
                  style={{
                    flex: 1,
                    padding: '12px',
                    background: 'var(--theme-error)',
                    border: 'none',
                    borderRadius: 12,
                    color: '#fff',
                    fontSize: 14,
                    fontWeight: 700,
                    cursor: isResetting ? 'wait' : 'pointer',
                  }}
                >
                  {isResetting ? 'Сброс...' : 'Сбросить урок'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toast */}
        {toastMessage && (
          <div style={{
            position: 'fixed',
            bottom: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            background: 'var(--theme-bg-card)',
            color: 'var(--theme-text)',
            border: '1px solid var(--theme-border)',
            padding: '10px 18px',
            borderRadius: 12,
            fontSize: 13,
            fontWeight: 600,
            zIndex: 1000,
            boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
            maxWidth: 'calc(100vw - 32px)',
          }}>
            {toastMessage}
          </div>
        )}
      </Layout>
    );
  }

  // ---------------------------------------------------------------------------
  // Главный экран каталога курсов
  // ---------------------------------------------------------------------------
  return (
    <Layout>
      <div style={{ padding: '20px 16px 80px' }}>
        <h1 style={{ margin: '0 0 6px', fontSize: 22, color: 'var(--theme-text)', fontWeight: 700 }}>
          Курсы
        </h1>
        <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--theme-text-secondary)' }}>
          Пошаговые программы и частотные наборы слов с автономным обучением
        </p>

        {/* Вкладки категорий */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          {[
            { id: 'all', label: 'Все' },
            { id: 'tops', label: '⭐ Топ-подборки' },
            { id: 'cefr', label: '🎓 Уровни CEFR' },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setCategoryFilter(tab.id as any)}
              style={{
                padding: '7px 14px',
                borderRadius: 999,
                border: `1px solid ${categoryFilter === tab.id ? 'var(--theme-primary)' : 'var(--theme-border)'}`,
                background: categoryFilter === tab.id ? 'rgba(255, 149, 0, 0.15)' : 'var(--theme-bg-elevated)',
                color: categoryFilter === tab.id ? 'var(--theme-primary)' : 'var(--theme-text-secondary)',
                fontSize: 13,
                fontWeight: categoryFilter === tab.id ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {isLoadingDetail && (
          <div style={{
            position: 'fixed', inset: 0, background: 'var(--theme-bg-overlay)',
            zIndex: 99, display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--theme-text)', fontSize: 15, fontWeight: 600,
          }}>
            Загрузка курса...
          </div>
        )}

        {filteredCourses.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--theme-text-muted)' }}>
            Курсы не найдены
          </div>
        ) : (
          filteredCourses.map(course => {
            const color = LEVEL_COLORS[course.level] ?? 'var(--theme-primary)';
            const emoji = LEVEL_EMOJIS[course.level] ?? '📚';
            const isLevelCourse = !course.slug.startsWith('top');

            // Для уровней CEFR на карточке каталога выделяем Экспресс (10 уроков / 150 слов) и Полный курс
            const totalLessons = course.total_lessons;
            const totalWords = course.total_words;
            const completedLessons = course.completed_lessons ?? 0;

            const expressLessons = isLevelCourse ? Math.min(totalLessons, 10) : totalLessons;
            const isFullDone = (completedLessons >= totalLessons && totalLessons > 0) || course.is_completed;
            const isExpressDone = isLevelCourse && (completedLessons >= expressLessons && expressLessons > 0);

            // Прогресс бар:
            // 1) Если курс полностью завершён — 100%
            // 2) Если пройден Экспресс (но не весь курс) — реальный % полного курса course.percent
            // 3) Если Экспресс в процессе — % уроков Экспресса
            // 4) Для остальных курсов (Top100/500) — процент завершения курса
            const barPercent = isFullDone
              ? 100
              : isExpressDone
                ? Math.max(course.percent, 4)
                : isLevelCourse
                  ? expressLessons > 0 ? Math.round((completedLessons / expressLessons) * 100) : 0
                  : course.percent;

            return (
              <button
                key={course.id}
                onClick={() => openCourse(course.id)}
                style={{
                  display: 'block',
                  width: '100%',
                  background: 'var(--theme-bg-card)',
                  border: isFullDone
                    ? '1.5px solid #10b981'
                    : isExpressDone
                      ? '1.5px solid rgba(16, 185, 129, 0.45)'
                      : '1px solid var(--theme-border)',
                  borderRadius: 16,
                  padding: '16px',
                  textAlign: 'left',
                  cursor: 'pointer',
                  marginBottom: 12,
                  boxShadow: '0 2px 10px rgba(0,0,0,0.06)',
                  transition: 'transform 0.1s ease',
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
                  <div style={{ display: 'flex', gap: 12, alignItems: 'center', minWidth: 0, flex: 1 }}>
                    <div style={{
                      fontSize: 26,
                      width: 42,
                      height: 42,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: `${color}18`,
                      borderRadius: 12,
                      flexShrink: 0,
                    }}>
                      {emoji}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{
                        fontWeight: 700,
                        fontSize: 16,
                        color: 'var(--theme-text)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {course.name}
                      </div>
                      <div style={{
                        fontSize: 12,
                        color: 'var(--theme-text-secondary)',
                        marginTop: 2,
                        lineHeight: 1.3,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                        {course.description}
                      </div>
                    </div>
                  </div>

                  <div style={{ flexShrink: 0 }}>
                    {isFullDone ? (
                      <div style={{
                        background: 'rgba(16, 185, 129, 0.15)',
                        color: '#10b981',
                        border: '1px solid rgba(16, 185, 129, 0.4)',
                        borderRadius: 8,
                        padding: '3px 8px',
                        fontSize: 11,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}>
                        🏆 Пройден
                      </div>
                    ) : isExpressDone ? (
                      <div style={{
                        background: 'rgba(16, 185, 129, 0.15)',
                        color: '#10b981',
                        border: '1px solid rgba(16, 185, 129, 0.4)',
                        borderRadius: 8,
                        padding: '3px 8px',
                        fontSize: 11,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}>
                        ⚡ Экспресс
                      </div>
                    ) : (
                      <div style={{
                        background: `${color}20`,
                        color,
                        border: `1px solid ${color}60`,
                        borderRadius: 8,
                        padding: '3px 8px',
                        fontSize: 11,
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}>
                        {course.level}
                      </div>
                    )}
                  </div>
                </div>

                {/* Прогресс-бар на карточке */}
                <div style={{ marginTop: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 6, fontSize: 11 }}>
                    <div style={{ minWidth: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: 'var(--theme-text-muted)' }}>
                      {isFullDone ? (
                        <span style={{ color: '#10b981', fontWeight: 600 }}>
                          🏆 Полный курс освоен ({totalLessons} ур. • 100%)
                        </span>
                      ) : isExpressDone ? (
                        <span>
                          <strong style={{ color: '#10b981' }}>⚡ Экспресс: 10/10</strong>
                          <span style={{ color: 'var(--theme-text-muted)' }}>
                            {' '}• Полный: {completedLessons}/{totalLessons} ур. ({course.percent}%)
                          </span>
                        </span>
                      ) : completedLessons > 0 ? (
                        <span>
                          <strong style={{ color: 'var(--theme-text)' }}>
                            {isLevelCourse
                              ? `⚡ Экспресс: Урок ${completedLessons + 1}/${expressLessons}`
                              : `📍 Урок ${completedLessons + 1}/${totalLessons}`}
                          </strong>
                          {' '}• {barPercent}%
                        </span>
                      ) : (
                        <span>
                          {isLevelCourse
                            ? `⚡ 10 ур. Экспресс • Всего ${totalLessons} ур.`
                            : `${totalLessons} уроков • ${totalWords} слов`}
                        </span>
                      )}
                    </div>
                    {course.due_reviews_count > 0 && (
                      <span style={{
                        flexShrink: 0,
                        whiteSpace: 'nowrap',
                        background: 'rgba(245, 158, 11, 0.12)',
                        color: '#f59e0b',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderRadius: 6,
                        padding: '2px 6px',
                        fontSize: 11,
                        fontWeight: 700,
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 3,
                      }}>
                        <span>🔄</span>
                        <span>{course.due_reviews_count}</span>
                      </span>
                    )}
                  </div>
                  <div style={{ height: 6, background: 'var(--theme-border)', borderRadius: 3, overflow: 'hidden' }}>
                    <div style={{
                      height: '100%',
                      background: isFullDone ? '#10b981' : color,
                      width: `${barPercent}%`,
                      borderRadius: 3,
                    }} />
                  </div>
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* Модальное окно подтверждения сброса урока */}
      {showResetModal !== null && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.7)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16,
          backdropFilter: 'blur(4px)',
        }}>
          <div style={{
            background: 'var(--theme-bg-elevated)',
            border: '1px solid var(--theme-border)',
            borderRadius: 20,
            padding: '24px 20px',
            maxWidth: 360,
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 12px 36px rgba(0, 0, 0, 0.4)',
          }}>
            <div style={{ fontSize: 44, marginBottom: 12 }}>🔄</div>
            <h3 style={{ fontSize: 18, fontWeight: 700, color: 'var(--theme-text)', marginBottom: 8 }}>
              Пройти Урок {showResetModal} заново?
            </h3>
            <p style={{ fontSize: 13, color: 'var(--theme-text-secondary)', lineHeight: 1.5, marginBottom: 20 }}>
              Прогресс по всем 15 словам этого урока будет сброшен в 0/15. Вы сможете пройти его с чистого листа: сначала Тест, затем Ввод на клавиатуре.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowResetModal(null)}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'var(--theme-bg-card)',
                  border: '1px solid var(--theme-border)',
                  borderRadius: 12,
                  color: 'var(--theme-text-secondary)',
                  fontSize: 14,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                Отмена
              </button>
              <button
                onClick={() => handleResetLesson(showResetModal)}
                disabled={isResetting}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'var(--theme-error)',
                  border: 'none',
                  borderRadius: 12,
                  color: '#fff',
                  fontSize: 14,
                  fontWeight: 700,
                  cursor: isResetting ? 'wait' : 'pointer',
                }}
              >
                {isResetting ? 'Сброс...' : 'Сбросить урок'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toastMessage && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'var(--theme-bg-card)',
          color: 'var(--theme-text)',
          border: '1px solid var(--theme-border)',
          padding: '10px 18px',
          borderRadius: 12,
          fontSize: 13,
          fontWeight: 600,
          zIndex: 1000,
          boxShadow: '0 8px 30px rgba(0,0,0,0.4)',
          maxWidth: 'calc(100vw - 32px)',
        }}>
          {toastMessage}
        </div>
      )}
    </Layout>
  );
}
